// 내보낸 뒤 줄 수정, 어느 줄을 고치는 중인가와 저장 안 한 입력을 지키는 규칙
//
// 화면이 아니라 규칙만 둔다. 줄 수정 영역은 2026-10-14 사용자 지시로 목록 아래 한 자리에서
// 그 rally 행 바로 아래로 옮겨졌고, 그때 함께 요구된 것이 둘이다. 한 번에 한 행만 열린다.
// 저장하지 않은 입력이 다른 행을 고르는 순간 조용히 사라지지 않는다. 이 두 규칙을 화면
// 코드 안의 조건문으로 흩어 두면 「같은 행을 다시 눌렀다」와 「다른 행을 눌렀다」의 갈래가
// 서로 다르게 낡으므로, 상태 전이를 이 파일 하나가 진다. 단위 검사가 이것을 직접 잰다.
//
// 브라우저 대화상자를 쓰지 않는다. 묻는 자리는 지금 열린 편집 영역 안이고(세션 선택),
// 그 영역이 「저장하지 않은 변경이 있습니다」와 버리고 옮기기, 계속 편집 둘을 그린다.

import { duration } from '../../../../core/time/brand.ts'
import { formatSignedOffset } from '../../../../core/timing/format.ts'
import { parseTimeText } from '../../../ui/time-input.tsx'

/** 묻는 중인 이동. 다른 행으로 옮기기, 또는 같은 행의 고치기를 다시 눌러 닫기. */
export type PendingMove = { kind: 'switch'; to: string } | { kind: 'close' }

export interface EditState {
  /** 지금 열린 줄. 없으면 `null`. 한 번에 하나뿐이라 배열이 아니다. */
  editingId: string | null
  /** 저장 안 한 입력 때문에 멈춰 둔 이동. 없으면 `null`. */
  pending: PendingMove | null
}

export const EDIT_IDLE: EditState = { editingId: null, pending: null }

/**
 * 어느 행의 고치기 버튼이 눌렸다.
 *
 * 닫혀 있으면 그 행을 연다. 같은 행이면 닫는다(토글). 다른 행이면 옮긴다. 다만 지금 열린
 * 영역에 저장 안 한 입력이 있으면 옮기거나 닫지 않고 멈춰 세운 뒤 묻는다. 이미 묻는 중에
 * 또 다른 행이 눌리면 물음의 대상만 그 행으로 바꾼다. 입력은 여전히 남아 있다.
 */
export function requestEdit(state: EditState, rallyId: string, dirty: boolean): EditState {
  if (state.editingId === null) return { editingId: rallyId, pending: null }
  if (rallyId === state.editingId) {
    return dirty ? { editingId: state.editingId, pending: { kind: 'close' } } : EDIT_IDLE
  }
  return dirty
    ? { editingId: state.editingId, pending: { kind: 'switch', to: rallyId } }
    : { editingId: rallyId, pending: null }
}

/** 물음에 「버리고 진행」으로 답했다. 멈춰 둔 이동을 그대로 한다. */
export function confirmPending(state: EditState): EditState {
  if (state.pending === null) return state
  return state.pending.kind === 'close' ? EDIT_IDLE : { editingId: state.pending.to, pending: null }
}

/** 물음에 「계속 편집」으로 답했다. 열린 줄과 입력은 그대로다. */
export function keepEditing(state: EditState): EditState {
  return state.pending === null ? state : { editingId: state.editingId, pending: null }
}

/** 저장 성공이나 취소로 닫는다. 이 둘은 사람이 그 영역 안에서 고른 것이라 묻지 않는다. */
export function closeEdit(): EditState {
  return EDIT_IDLE
}

/**
 * 저장 안 한 입력을 가르는 기준. 영역을 연 순간 서버가 들고 있던 그 줄의 값이고, 열린 동안 서버 값이
 * 바뀌면 `followServer` 가 옮긴다. 매 렌더의 서버 값을 그대로 기준으로 쓰면 다른 창의 저장이 오는
 * 순간 아무것도 안 친 영역이 「저장 안 한 입력 있음」이 된다(2026-10-14 반증 검토).
 */
export interface EditBaseline {
  offsetMs: number
  rallyingMs: number
  rosterId: string
}

/** 편집 영역에 지금 들어 있는 값. offset 은 사람이 친 글자 그대로다. */
export interface EditDraft {
  offsetText: string
  rallyingMs: number
  rosterId: string
}

/**
 * 저장하지 않은 입력이 있는가.
 *
 * offset 은 글자가 아니라 값으로 견준다. `+0:02` 와 `+0:02.0` 은 같은 값이라 그 차이로
 * 물음을 띄우면 아무것도 안 고친 사람에게 「변경이 있습니다」라고 말하게 된다. 다만 형식이
 * 아닌 글자는 값이 없으니 고친 것으로 본다. 지우면 사람이 친 것이 사라진다.
 *
 * main 은 offset 칸이 없어 `isMain` 이면 offset 을 보지 않는다.
 */
export function isDirty(baseline: EditBaseline, draft: EditDraft, isMain: boolean): boolean {
  if (draft.rosterId !== baseline.rosterId) return true
  if (draft.rallyingMs !== baseline.rallyingMs) return true
  if (isMain) return false
  const parsed = parseTimeText(draft.offsetText)
  if (!parsed.ok) return true
  return parsed.millis !== baseline.offsetMs
}

/**
 * 열린 동안 서버의 그 줄 값이 바뀌었다(다른 창의 저장 방송). 사람이 손대지 않은 칸은 새 값을
 * 따라가고, 손댄 칸은 사람이 친 값을 그대로 둔다. 기준은 새 값으로 옮긴다.
 *
 * 왜 따라가는가. 칸에 옛 값이 남은 채로 저장을 누르면 다른 창의 변경을 아무 말 없이 덮어쓴다.
 * 손댄 칸까지 바꾸면 사람이 친 것이 조용히 사라진다. 그래서 칸마다 가른다(세션 선택).
 *
 * `changed` 는 사람이 보던 값과 다른 새 값이 하나라도 왔는가다. 이 창이 방금 저장한 값이 돌아온
 * 경우(새 값이 칸의 값과 같다)는 바뀐 것으로 치지 않는다. 그래야 자기 저장 직후에 「다른 곳에서
 * 바뀌었습니다」가 뜨지 않는다.
 */
export function followServer(
  base: EditBaseline,
  draft: EditDraft,
  next: EditBaseline,
  isMain: boolean,
): { base: EditBaseline; draft: EditDraft; changed: boolean } {
  let changed = false
  let rosterId = draft.rosterId
  if (next.rosterId !== base.rosterId) {
    if (draft.rosterId === base.rosterId) rosterId = next.rosterId
    if (next.rosterId !== draft.rosterId) changed = true
  }
  let rallyingMs = draft.rallyingMs
  if (next.rallyingMs !== base.rallyingMs) {
    if (draft.rallyingMs === base.rallyingMs) rallyingMs = next.rallyingMs
    if (next.rallyingMs !== draft.rallyingMs) changed = true
  }
  let offsetText = draft.offsetText
  if (!isMain && next.offsetMs !== base.offsetMs) {
    const parsed = parseTimeText(draft.offsetText)
    const typed = parsed.ok ? parsed.millis : null
    if (typed === base.offsetMs) offsetText = formatSignedOffset(duration(next.offsetMs))
    if (typed !== next.offsetMs) changed = true
  }
  return { base: next, draft: { offsetText, rallyingMs, rosterId }, changed }
}

/**
 * 편집 영역을 가리지 않는 자리로 옮기려면 문서를 얼마나 굴려야 하는가(픽셀, 아래로 +).
 *
 * 위에는 끈적이는 상단 고정줄이, 아래에는 끈적이는 다시 잡기 바가 있다. 둘 사이가 보이는
 * 칸이다(`viewTop`, `viewBottom`, 화면 좌표). 행과 편집 영역을 한 덩어리(`top`, `bottom`)로
 * 본다.
 *
 * 다 들어가면 모자라는 쪽만큼만 굴린다. 이미 보이면 0 이라 데스크톱에서 화면이 괜히 뛰지
 * 않는다. 다 들어가지 않으면 덩어리의 머리(행과 닉네임, 첫 입력칸)를 보이는 칸 맨 위에
 * 맞춘다. 저장과 취소는 그 아래에 있고 사람이 굴려 내려가면 바가 끝까지 따라와도 가리지
 * 않는다. 바는 제자리(목록 아래)에 닿으면 멈추기 때문이다.
 */
export function revealDelta(
  box: { top: number; bottom: number },
  view: { viewTop: number; viewBottom: number },
): number {
  const room = view.viewBottom - view.viewTop
  const height = box.bottom - box.top
  if (height > room) return box.top - view.viewTop
  if (box.top < view.viewTop) return box.top - view.viewTop
  if (box.bottom > view.viewBottom) return box.bottom - view.viewBottom
  return 0
}
